import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { HolidayValue } from '@/constants/holidays';
import { DisplaySchedule } from '@/types/display';
import {
  loadDisplayHolidayYear,
  upsertDisplayHolidayYear,
  saveHolidayContent,
  HolidayContent
} from '@/services/displayHolidayYearService';

type DisplayHolidayYear = {
  id: string;
  display_id: number;
  holiday: string;
  year: number;
  description: string | null;
  photos: unknown;
  videos: unknown;
  schedule: unknown;
  created_at: string;
  updated_at: string;
  user_id: string | null;
};

interface DisplaySong {
  id: string;
  title: string;
  artist: string;
  duration: string | null;
  year_introduced: number;
  video_url: string | null;
  album_cover_url: string | null;
  display_holiday_year_id: string;
}

/**
 * Hook to fetch and cache holiday year data
 * Automatically caches by displayId + holiday + year
 */
export const useHolidayYear = (
  displayId: number,
  holiday: HolidayValue,
  year: number = new Date().getFullYear()
) => {
  return useQuery({
    queryKey: ['holidayYear', displayId, holiday, year],
    queryFn: async (): Promise<DisplayHolidayYear | null> => {
      return await loadDisplayHolidayYear(displayId, holiday, year);
    },
    enabled: !!displayId && !!holiday,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });
};

/**
 * Hook to fetch and cache songs for a holiday year
 * Automatically caches by holiday year ID
 */
export const useHolidaySongs = (holidayYearId: string | null) => {
  return useQuery({
    queryKey: ['holidaySongs', holidayYearId],
    queryFn: async (): Promise<DisplaySong[]> => {
      if (!holidayYearId) return [];

      const { data, error } = await supabase
        .from('display_songs')
        .select('*')
        .eq('display_holiday_year_id', holidayYearId)
        .order('year_introduced', { ascending: false });

      if (error) {
        console.error('Error loading holiday songs:', error);
        throw error;
      }

      return (data || []).map(song => ({
        id: song.id,
        title: song.title,
        artist: song.artist,
        duration: song.duration || null,
        year_introduced: song.year_introduced,
        video_url: song.video_url || null,
        album_cover_url: song.album_cover_url || null,
        display_holiday_year_id: holidayYearId
      }));
    },
    enabled: !!holidayYearId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

/**
 * Mutation hook for creating/updating holiday year records
 * Automatically invalidates relevant queries on success
 */
export const useUpsertHolidayYear = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      displayId,
      holiday,
      year,
      content
    }: {
      displayId: number;
      holiday: HolidayValue;
      year?: number;
      content?: HolidayContent;
    }) => {
      return await upsertDisplayHolidayYear(displayId, holiday, year, content);
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch holiday year query
      queryClient.invalidateQueries({
        queryKey: ['holidayYear', variables.displayId, variables.holiday, variables.year || new Date().getFullYear()]
      });
    },
  });
};

/**
 * Mutation hook for saving holiday content
 * Automatically invalidates relevant queries on success
 */
export const useSaveHolidayContent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      holidayYearId,
      content
    }: {
      holidayYearId: string;
      content: HolidayContent;
    }) => {
      return await saveHolidayContent(holidayYearId, content);
    },
    onSuccess: (data) => {
      // Invalidate the specific holiday year query
      queryClient.invalidateQueries({
        queryKey: ['holidayYear', data.display_id, data.holiday, data.year]
      });
    },
  });
};

/**
 * Mutation hook for saving songs
 * Automatically invalidates songs query on success
 */
export const useSaveSongs = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      holidayYearId,
      songs
    }: {
      holidayYearId: string;
      songs: Array<{
        id?: string;
        title: string;
        artist: string;
        duration?: string;
        year_introduced: number;
        video_url?: string;
        album_cover_url?: string;
      }>;
    }) => {
      // Filter valid songs
      const validSongs = songs.filter(song =>
        song.title?.trim() && song.artist?.trim() && song.year_introduced
      );

      if (validSongs.length === 0) {
        throw new Error('Please add at least one complete song');
      }

      // Separate existing songs from new songs
      const existingSongs = validSongs.filter(song => song.id);
      const newSongs = validSongs.filter(song => !song.id);

      // Update existing songs
      for (const song of existingSongs) {
        await supabase
          .from('display_songs')
          .update({
            title: song.title.trim(),
            artist: song.artist.trim(),
            duration: song.duration?.trim() || null,
            year_introduced: song.year_introduced,
            video_url: song.video_url?.trim() || null,
            album_cover_url: song.album_cover_url?.trim() || null,
          })
          .eq('id', song.id!);
      }

      // Insert new songs
      if (newSongs.length > 0) {
        const songsToInsert = newSongs.map(song => ({
          title: song.title.trim(),
          artist: song.artist.trim(),
          duration: song.duration?.trim() || null,
          year_introduced: song.year_introduced,
          video_url: song.video_url?.trim() || null,
          album_cover_url: song.album_cover_url?.trim() || null,
          display_holiday_year_id: holidayYearId
        }));

        await supabase.from('display_songs').insert(songsToInsert);
      }

      return validSongs;
    },
    onSuccess: (_data, variables) => {
      // Invalidate songs query to refetch updated list
      queryClient.invalidateQueries({
        queryKey: ['holidaySongs', variables.holidayYearId]
      });
    },
  });
};
